import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import moment from "moment";
import { Clock, Users } from "lucide-react";
import { getQueue } from "../../lib/display-api";

const SAFETY_POLL_MS = 60_000;
const MAX_VISIBLE_PER_DOCTOR = 5;

// The queue board pages through doctors VISIBLE_CARDS at a time, each page
// on screen for Settings → Reception Display's "seconds per page"
// (DEFAULT_PAGE_SECONDS until the first fetch lands). After the last page, "both" mode goes back
// to the video; otherwise it starts again from the first page.
const VISIBLE_CARDS = 4;
const CARD_GAP_REM = 1.5;
const DEFAULT_PAGE_SECONDS = 20;
const PAGE_FADE_MS = 900;

// A cycling accent per doctor card — purely visual, so patients can tell
// cards apart at a glance from across a waiting room.
const ACCENTS = [
  { bar: "bg-primary", chip: "bg-primary/10 text-primary" },
  { bar: "bg-sky-500", chip: "bg-sky-500/10 text-sky-600" },
  { bar: "bg-amber-500", chip: "bg-amber-500/10 text-amber-600" },
  { bar: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-600" },
  { bar: "bg-rose-500", chip: "bg-rose-500/10 text-rose-600" },
  { bar: "bg-violet-500", chip: "bg-violet-500/10 text-violet-600" },
  { bar: "bg-teal-500", chip: "bg-teal-500/10 text-teal-600" },
  { bar: "bg-secondary", chip: "bg-secondary/10 text-secondary" },
];

// Full-screen, no-login reception board — deliberately outside <Layout>
// (no sidebar/header) since this renders on a TV, not for a logged-in staff
// member. Auth is a credential baked into this page's own URL — a short
// ?code= (current format, typeable on a TV remote) or a legacy ?token= JWT —
// not the normal dashboard session. See src/lib/display-api.js.
//
// Two internal render states, "video" and "queue" (`mode`/`modeRef` below):
// an optional branding video (a file path set in Settings → Reception
// Display, served from medipro-dashboard/public) loops full-screen via a
// plain <video> element; the queue board takes over for 30s whenever the
// video finishes a loop, or the instant a check-in/booking/completion
// happens, then returns to the video. The <video> element stays mounted
// across mode changes (just hidden) so pausing it for an interrupt and
// resuming it later continues from the same spot rather than restarting.
//
// That alternation is itself gated by `displayMode` (`display.mode` in
// Settings, "video" | "queue" | "both") — an operator-facing switch that
// pins the board to just the video, just the queue, or lets it alternate
// as above ("both", the historical default).
export default function QueueDisplay() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const token = searchParams.get("token");

  const [queue, setQueue] = useState([]);
  const [videoPath, setVideoPath] = useState("");
  const [displayMode, setDisplayMode] = useState("both");
  const [pageSeconds, setPageSeconds] = useState(DEFAULT_PAGE_SECONDS);
  const [loaded, setLoaded] = useState(false);
  // null (not the device's current time) until the first fetch resolves —
  // see the "Live clock" effect below for why this never falls back to the
  // device's own clock, even briefly.
  const [now, setNow] = useState(null);
  const [error, setError] = useState("");
  const [videoFailed, setVideoFailed] = useState(false);
  const [mode, setMode] = useState("video");
  const socketRef = useRef(null);
  const videoRef = useRef(null);
  const modeRef = useRef(mode);
  const displayModeRef = useRef(displayMode);
  // The server's clock at the moment of the last successful fetch, paired
  // with performance.now() (a monotonic timer, NOT the device's wall
  // clock — immune to timezone/DST/manual changes and NTP corrections
  // jumping mid-session) at that same instant. Ticking forward from this
  // pair (see the "Live clock" effect below) never reads the device's own
  // wall clock at all — see that effect's comment for why.
  const serverEpochAtFetchRef = useRef(null);
  const perfAtFetchRef = useRef(0);

  // "queue" mode never shows the video regardless of what's configured;
  // "video" and "both" both allow it (they differ in whether the board ever
  // switches away to show the queue — see handleUpdate/handleVideoEnded).
  const hasVideo = !!videoPath && !videoFailed && displayMode !== "queue";

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    displayModeRef.current = displayMode;
  }, [displayMode]);

  // Clinic name and video path travel over this same public, code/token-
  // authenticated endpoint as the queue itself (see controllers/display/
  // queue.js) — never the admin-only /api/v1/admin/settings, which requires
  // a logged-in staff session that a kiosk/TV never has.
  const refetch = useCallback(() => {
    if (!code && !token) return;
    getQueue({ code, token })
      .then((res) => {
        const newDisplayMode = res.displayMode || "both";
        // The operator just changed Settings → Reception Display itself
        // (not a routine appointment update, which never changes this) —
        // react immediately rather than leaving the board on whatever it
        // was already showing until a video happens to end or another
        // event lands. "queue" forces away from video right now; "video"/
        // "both" both bring video back right now (an appointment event
        // later still triggers "both"'s normal queue interlude as usual).
        if (newDisplayMode !== displayModeRef.current) {
          setMode(newDisplayMode === "queue" ? "queue" : "video");
        }
        setQueue(res.data || []);
        setVideoPath(res.videoPath || "");
        setDisplayMode(newDisplayMode);
        setPageSeconds(res.pageSeconds || DEFAULT_PAGE_SECONDS);
        // Re-anchored on every fetch (not just once), so even performance.now()
        // drift (rare, but it can happen over a long enough session) gets
        // corrected too, not just a one-off wrong device clock.
        if (res.serverTime) {
          serverEpochAtFetchRef.current = new Date(res.serverTime).getTime();
          perfAtFetchRef.current = performance.now();
        }
        setError("");
        setLoaded(true);
      })
      .catch((err) => setError(err?.response?.data?.message || "Could not load the queue."));
  }, [code, token]);

  // Switches to the queue board — used both when the video finishes
  // naturally and when a real-time update interrupts it early. The board
  // returns to the video itself once it has shown every page (see the page
  // timer below).
  const startQueueInterlude = useCallback(() => {
    refetch();
    setMode("queue");
  }, [refetch]);

  // Unlike the old YouTube player's playVideo() (which restarted an ended
  // video automatically), a native <video> just sits on its last frame after
  // ending — rewind explicitly so the next mode-flip back to "video" actually
  // loops instead of resuming at the end.
  const handleVideoEnded = useCallback(() => {
    if (videoRef.current) videoRef.current.currentTime = 0;
    // "video only" must never show the queue, not even for the usual
    // interlude — that's the one thing distinguishing it from "both". Just
    // loop the video again instead (the [mode, hasVideo] play-effect above
    // won't fire here since neither actually changes).
    if (displayModeRef.current === "video") {
      videoRef.current?.play().catch(() => {});
      return;
    }
    startQueueInterlude();
  }, [startQueueInterlude]);

  // No usable video (none configured, or it failed to load) — stay on the
  // queue board permanently instead of getting stuck on a blank video mode.
  // Gated on `loaded` so the brief moment before the first queue fetch
  // resolves (videoPath is still "" simply because nothing has loaded yet,
  // not because there's genuinely no video) doesn't permanently lock mode
  // into "queue" before the real video ever gets a chance to show.
  useEffect(() => {
    if (loaded && !hasVideo) setMode("queue");
  }, [loaded, hasVideo]);

  // Resets the failed flag whenever the configured path changes, so a new
  // video gets a fresh chance to load instead of staying stuck on "queue"
  // from a previous path's failure.
  useEffect(() => {
    setVideoFailed(false);
  }, [videoPath]);

  // Drive playback purely off `mode`: play() resumes a paused video from
  // where it left off (the interrupt case) or restarts one that's already
  // ended (the natural-loop case) — the same call correctly covers both.
  // Tries unmuted first — on a kiosk browser launched with an autoplay
  // policy override (see Settings help text) this plays with sound; on a
  // browser that still blocks unmuted autoplay, it falls back to muted
  // rather than never starting at all.
  useEffect(() => {
    if (mode !== "video" || !hasVideo || !videoRef.current) return;
    const video = videoRef.current;
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => setVideoFailed(true));
    });
  }, [mode, hasVideo]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Real-time: the instant a check-in/complete/cancel/edit/booking happens
  // anywhere (or the video is replaced from Settings), refresh the queue
  // (which now also carries clinic name/video path — see refetch above) and
  // either interrupt the video for the queue board (if it's currently
  // playing) or just silently refresh the data underneath it (if the queue
  // board is already showing). Also fires on (re)connect so a WiFi blip
  // self-heals immediately rather than waiting for the safety poll.
  useEffect(() => {
    if (!code && !token) return;
    // path matches the server's — nested under /api/, the only nginx
    // location proxying WebSocket upgrades in production (see server.js).
    const socket = io(process.env.REACT_APP_BACKEND_URL, { path: "/api/socket.io" });
    socketRef.current = socket;

    const handleUpdate = () => {
      // Pinned to "video" — refresh the data underneath (in case the mode
      // itself just changed) but never interrupt playback for a queue
      // interlude the operator has explicitly turned off.
      if (displayModeRef.current === "video") {
        refetch();
        return;
      }
      if (modeRef.current === "video") {
        videoRef.current?.pause();
        startQueueInterlude();
      } else {
        refetch();
      }
    };

    socket.on("connect", refetch);
    socket.on("queue:updated", handleUpdate);
    return () => socket.disconnect();
  }, [code, token, refetch, startQueueInterlude]);

  // Safety net in case a socket event was ever missed.
  useEffect(() => {
    const interval = setInterval(refetch, SAFETY_POLL_MS);
    return () => clearInterval(interval);
  }, [refetch]);

  // Live clock — never reads the device's own wall clock. A kiosk TV/stick
  // with a wrong or drifted system time would otherwise silently compute
  // the wrong "next" patient with no visible error (this happened: a TV
  // stuck at 4:50 AM instead of 2:30 PM showed every doctor's first
  // appointment of the day as "next", never advancing). Instead, this
  // walks forward from the server's clock at the last fetch
  // (serverEpochAtFetchRef) by however much *monotonic* time has passed
  // since then (performance.now() minus perfAtFetchRef — never the
  // device's Date.now()/wall clock, so a manual clock change or NTP
  // correction mid-session can't cause a jump either) — both the
  // displayed time and the byDoctor "next" computation below are fully
  // anchored to the server this way. Before the first fetch resolves,
  // serverEpochAtFetchRef is still null, so this skips rather than
  // falling back to device time.
  useEffect(() => {
    const interval = setInterval(() => {
      if (serverEpochAtFetchRef.current == null) return;
      const elapsed = performance.now() - perfAtFetchRef.current;
      setNow(new Date(serverEpochAtFetchRef.current + elapsed));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Grouped by doctor — the queue endpoint already returns rows sorted by
  // slot time, so each doctor's full schedule stays in order within its
  // group without re-sorting here. "Next" is then derived purely from the
  // clock, not check-in/completion status: whoever's slot time has most
  // recently arrived is treated as "with the doctor now" (not shown), and
  // NEXT is whoever comes right after them — so at 6:00 for a 6:00/6:10/6:20
  // schedule, NEXT shows 6:10's patient (not 6:00's), until 6:10 arrives and
  // it advances again. Before the day's first slot, NEXT is just that first
  // slot. Recomputes every tick of `now` so it advances on its own, without
  // needing a booking/check-in event to trigger a refetch. Doctor cards are
  // ordered by whichever doctor's NEXT is soonest, and a doctor whose whole
  // day has already passed drops off the board entirely.
  const byDoctor = useMemo(() => {
    if (!now) return []; // server time not known yet — show nothing rather than guess
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const groups = new Map();
    queue.forEach((row) => {
      const list = groups.get(row.doctorName) || [];
      list.push(row);
      groups.set(row.doctorName, list);
    });
    return [...groups.entries()]
      .map(([doctorName, allPatients]) => {
        let currentIndex = -1;
        allPatients.forEach((p, idx) => {
          if (p.slotMinutes <= nowMinutes) currentIndex = idx;
        });
        return { doctorName, patients: allPatients.slice(currentIndex + 1) };
      })
      .filter((group) => group.patients.length > 0)
      .sort((a, b) => a.patients[0].slotMinutes - b.patients[0].slotMinutes);
  }, [queue, now]);

  // Paged in name order rather than byDoctor's "soonest next" order, which
  // reshuffles as slot times pass and could show a doctor twice (or skip
  // one) between one page and the next.
  const pagedDoctors = useMemo(
    () => [...byDoctor].sort((a, b) => a.doctorName.localeCompare(b.doctorName)),
    [byDoctor]
  );
  const pageCount = Math.max(1, Math.ceil(pagedDoctors.length / VISIBLE_CARDS));
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;

  const showQueue = mode === "queue" || !hasVideo;

  // A tick per page shown, not the page index itself: on a single page the
  // index never changes, and the timer below re-arms off this changing.
  const [pageTick, setPageTick] = useState(0);
  const pageIndex = pageTick % pageCount;

  useEffect(() => {
    if (showQueue) setPageTick(0);
  }, [showQueue]);

  // Read from pageCountRef rather than a dependency, so a routine data
  // refresh doesn't restart the current page's timer.
  useEffect(() => {
    if (!showQueue) return;
    const timer = setTimeout(() => {
      const onLastPage = pageTick % pageCountRef.current === pageCountRef.current - 1;
      if (onLastPage && modeRef.current === "queue" && displayModeRef.current === "both" && hasVideo) {
        setMode("video");
      } else {
        setPageTick(pageTick + 1);
      }
    }, pageSeconds * 1000);
    return () => clearTimeout(timer);
  }, [showQueue, pageTick, hasVideo, pageSeconds]);

  if (!code && !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-8 text-center">
        <p className="text-lg text-muted-foreground">
          This page needs a display code in its URL — run{" "}
          <code className="rounded bg-muted px-1.5 py-0.5">npm run generate:display-token</code> on the backend and
          use the link it prints.
        </p>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black text-foreground">
      {videoPath && displayMode !== "queue" && (
        <video
          ref={videoRef}
          src={`${process.env.PUBLIC_URL}${videoPath}`}
          className={`absolute inset-0 h-full w-full object-cover ${showQueue ? "hidden" : ""}`}
          playsInline
          loop={displayMode === "video"}
          onEnded={handleVideoEnded}
          onError={() => setVideoFailed(true)}
        />
      )}

      <div className={`absolute inset-0 flex flex-col bg-background px-10 pb-8 pt-7 ${showQueue ? "" : "hidden"}`}>
        <div className="flex items-end justify-between border-b border-border pb-5">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-secondary" />
            </span>
            <span className="text-lg tracking-wide">Live — who's next, doctor by doctor</span>
          </div>
          <div className="text-right">
            <p className="text-4xl font-semibold tabular-nums tracking-tight">
              {now ? moment(now).format("h:mm:ss A") : "--:--:--"}
            </p>
            <p className="mt-1 text-base text-muted-foreground">
              {now ? moment(now).format("dddd, D MMMM YYYY") : "Syncing…"}
            </p>
          </div>
        </div>

        {error && <p className="mt-4 text-destructive">{error}</p>}

        <div className="mt-8 flex min-h-0 flex-1 flex-col">
          {byDoctor.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-border">
              <Users className="h-12 w-12 text-muted-foreground/50" />
              <p className="text-3xl text-muted-foreground">
                {loaded ? "No one waiting right now." : "Syncing with server…"}
              </p>
            </div>
          ) : (
            <DoctorPages groups={pagedDoctors} pageIndex={pageIndex} pageTick={pageTick} pageSeconds={pageSeconds} active={showQueue} />
          )}
        </div>
      </div>
    </div>
  );
}

// Every page is mounted at once, stacked, and cross-fades with a slight
// drift in the direction of travel — the outgoing page fades out as the
// next fades in, rather than the screen blanking between them. A short last
// page is centred.
function DoctorPages({ groups, pageIndex, pageTick, pageSeconds, active }) {
  const pages = [];
  for (let i = 0; i < groups.length; i += VISIBLE_CARDS) pages.push(groups.slice(i, i + VISIBLE_CARDS));
  const cardWidth = `calc((100% - ${(VISIBLE_CARDS - 1) * CARD_GAP_REM}rem) / ${VISIBLE_CARDS})`;

  return (
    <>
      <div className="relative min-h-0 flex-1">
        {pages.map((page, p) => {
          const current = p === pageIndex;
          return (
            <div
              key={p}
              className="absolute inset-0 flex justify-center"
              aria-hidden={!current || undefined}
              style={{
                gap: `${CARD_GAP_REM}rem`,
                opacity: current ? 1 : 0,
                transform: current ? "none" : `translateX(${p < pageIndex ? -2 : 2}rem)`,
                transition: `opacity ${PAGE_FADE_MS}ms ease, transform ${PAGE_FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                pointerEvents: current ? undefined : "none",
              }}
            >
              {page.map((group) => {
                const index = groups.indexOf(group);
                return (
                  <div key={group.doctorName} className="h-full shrink-0" style={{ width: cardWidth }}>
                    <DoctorCard {...group} accent={ACCENTS[index % ACCENTS.length]} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {pages.length > 1 && (
        <div className="mt-6 flex items-center justify-between gap-8">
          <div className="flex items-center gap-2">
            {pages.map((_, p) => (
              <span
                key={p}
                className={`h-1.5 rounded-full transition-all duration-700 ${
                  p === pageIndex ? "w-8 bg-foreground/70" : "w-1.5 bg-foreground/15"
                }`}
              />
            ))}
            <span className="ml-3 text-sm tabular-nums text-muted-foreground">
              {pageIndex + 1} / {pages.length}
            </span>
          </div>
          <div className="h-0.5 w-48 overflow-hidden rounded-full bg-foreground/10">
            {active && (
              <div
                key={pageTick}
                className="h-full origin-left rounded-full bg-foreground/40"
                style={{ animation: `queue-progress ${pageSeconds}s linear forwards` }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function DoctorCard({ doctorName, patients, accent }) {
  const [next, ...rest] = patients;
  const overflow = rest.length - MAX_VISIBLE_PER_DOCTOR + 1;
  const upcoming = overflow > 0 ? rest.slice(0, MAX_VISIBLE_PER_DOCTOR - 1) : rest;
  const accentText = accent.chip.split(" ")[1];

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(0,0,0,0.04),0_18px_40px_-20px_rgba(0,0,0,0.18)]">
      <div className={`h-1.5 shrink-0 ${accent.bar}`} />
      <div className="flex min-h-0 flex-1 flex-col p-6">
        {/* Room for a two-line name, so every card's sections line up */}
        <div className="flex min-h-[5.75rem] items-center">
          <p className="min-w-0 break-words text-4xl font-bold leading-tight tracking-tight">{doctorName}</p>
        </div>
        <span className={`mt-3 self-start rounded-full px-3.5 py-1 text-base font-semibold ${accent.chip}`}>
          {patients.length} waiting
        </span>

        <div className="mt-5 rounded-2xl bg-muted/60 px-5 py-3.5">
          <p className={`text-sm font-bold uppercase tracking-[0.2em] ${accentText}`}>Next</p>
          <p className="mt-1 break-words text-3xl font-semibold leading-tight tracking-tight">{next.patientName}</p>
          <p className="mt-1 flex items-center gap-2 text-xl tabular-nums text-muted-foreground">
            <Clock className="h-5 w-5" aria-hidden="true" />
            {next.slotTime}
          </p>
        </div>

        {upcoming.length > 0 && (
          <>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Then</p>
            <ul className="mt-2 divide-y divide-border/70">
              {upcoming.map((row) => (
                <li key={row.bookingId} className="flex items-baseline justify-between gap-4 py-2.5 text-2xl">
                  <span className="min-w-0 break-words">{row.patientName}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{row.slotTime}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {overflow > 0 && <p className="mt-auto pt-3 text-base text-muted-foreground">+{overflow} more waiting</p>}
      </div>
    </div>
  );
}
